import type { Environment } from "./environment";
import {
  createPersistedValue,
  type PersistedValueCodec,
} from "./persisted-value";

const SESSION_STORAGE_KEY = "redo_session";
const DEFAULT_SESSION_TIMEOUT_MS = 30 * 60 * 1000;

interface StoredSession {
  id: string;
  lastActivityAt: number;
}

const sessionCodec: PersistedValueCodec<StoredSession> = {
  parse(raw) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const parsed = JSON.parse(raw) as Partial<StoredSession>;
      if (
        typeof parsed?.id === "string" &&
        typeof parsed.lastActivityAt === "number"
      ) {
        return { id: parsed.id, lastActivityAt: parsed.lastActivityAt };
      }
    } catch {
      // ignore
    }
    return null;
  },
  serialize: (value) => JSON.stringify(value),
};

export interface SessionManager {
  resolve(now: number): Promise<string>;
}

export function createSessionManager(
  environment: Environment,
  timeoutMs: number = DEFAULT_SESSION_TIMEOUT_MS,
): SessionManager {
  const state = createPersistedValue(
    environment,
    { key: SESSION_STORAGE_KEY },
    sessionCodec,
  );

  return {
    async resolve(now) {
      await state.whenReady;
      const current = state.get();
      const next: StoredSession =
        !current || now - current.lastActivityAt > timeoutMs
          ? { id: environment.randomUUID(), lastActivityAt: now }
          : { id: current.id, lastActivityAt: now };
      state.set(next);
      return next.id;
    },
  };
}
